<template>
  <div class="details-component">
    <div class="details-component__header">
      <h1 v-if="form.id">{{ $t('add_permit.edit_permit') }} </h1>
      <h1 v-else>{{ $t('add_permit.new_permit') }} </h1>
    </div>
    <div class="details-component__content content">
      <form class="form">
        <fieldset class="form__fieldset">
          <div class="form__field" :class="{'form__field_error' : errors.has('parkingCardNr') || errors.has('selectedUser')}">
            <label for="parkingCardNr" class="form__label">{{ $t('add_permit.parking_card_id') }}</label>
            <input type="text" class="form__input"
                    name="parkingCardNr"
                    id="parkingCardNr"
                    v-model="form.parkingCardNr"
                    v-validate="'validParkingCardNr'"
                    :data-vv-as="$t('add_permit.parking_card_id')"
                    data-vv-delay="1000" ref="parkingCardNr"/>
            <tooltip v-show="errors.has('parkingCardNr')" class="form__validation">{{ $t('errors.valid_card_field') }}</tooltip>
            <tooltip v-show="errors.has('vehicleReg')" class="form__validation">{{ $t('errors.card_user_field_required') }}</tooltip>
          </div>
          <div class="form__field" v-if="isLandlord()" :class="{'form__field_error' : errors.has('selectedTenant')}">
            <label for="tenant" class="form__label">{{ $t('common.tenant') }}</label>
            <el-select name="selectedTenant"
              v-model="selectedTenant"
              v-validate="'selectedTenant'"
              :data-vv-as="$t('common.tenant')"
              v-if="isNew && tenants.items"
              placeholder="">
            <el-option
              v-for="tenant in tenants.items.filter(t => t.permitPack)"
              :key="tenant.id"
              :label="tenant.name"
              :value="tenant.id">
            </el-option>
          </el-select>
            <span v-else-if="permit.partner">{{ permit.partner.name }}</span>
            <tooltip v-show="errors.has('selectedTenant')" class="form__validation">{{ $t('errors.tenant_field_required') }}</tooltip>
          </div>
          <!-- Users -->
          <div class="form__field" v-if="selectedTenant || isTenant()" :class="{'form__field_error' : errors.has('selectedUser')}">
            <label for="user" class="form__label">{{ $t('common.user') }}</label>
            <el-select name="selectedUser"
              v-model="selectedUser"
              v-validate="'userOrParkingCardNrRequired'"
              :data-vv-as="$t('common.user')"
              ref="selectedUser"
              placeholder="">
              <el-option
                v-for="user of users.items"
                :key="user.id"
                :label="user.name"
                :value="user.id">
              </el-option>
            </el-select>
            <a v-if="selectedUser" class="disabled link" v-on:click="clearUser">
              <i class="icon icon--cross"></i>
            </a>
            <tooltip v-show="errors.has('selectedUser')" class="form__validation">{{ $t('errors.card_user_field_required') }}</tooltip>
          </div>
          <!-- Parking spot number -->
          <div class="form__field" :class="{'form__field_error' : errors.has('parkingSpotNumber')}">
            <label for="parkingSpotNumber" class="form__label">{{ $t('permits.parking_spot_number') }}</label>
            <input type="text" class="form__input"
                    name="parkingSpotNumber"
                    id="parkingSpotNumber"
                    v-model="form.parkingSpotNumber"
                    v-validate="'max:255'"
                    :data-vv-as="$t('permits.parking_spot_number')"
                    ref="parkingSpotNumber"/>
            <tooltip v-show="errors.has('parkingSpotNumber')" class="form__validation">{{ $tc('errors.field_max', 1, { max: 255, have: form.parkingSpotNumber ? form.parkingSpotNumber.length : '' }) }}</tooltip>
          </div>
          <!-- Vehicles -->
          <div class="form__field" v-if="selectedUser || isTenant()">
             <label for="user" class="form__label">{{ $tc('common.user_vehicle_reg', 0) }}</label>
            <el-select name="selectedVehicle"
              v-model="selectedVehicle"
              ref="selectedVehicle"
              placeholder="">
              <el-option
                v-for="vehicle of vehicles.items"
                :key="vehicle.id"
                :label="vehicle.vehicleReg"
                :value="vehicle.id">
              </el-option>
            </el-select>
            <a v-if="selectedVehicle" class="disabled link" v-on:click="clearVehicle">
              <i class="icon icon--cross"></i>
            </a>
          </div>
          <!-- Products -->
          <div class="form__field form__field_radiobuttons" :class="{'form__field_error' : errors.has('product')}" v-show="tenantHasProducts">
            <label for="product" class="form__label">{{ $tc('add_permit.product', 'product.total') }}</label>
            <div class="radiobuttons">
              <template v-for="product in products.items">
                <div :key="product.id" class="radiobuttons__element" v-if="canAddPermitTo(product)">
                  <label>
                    <input type="radio" name="product" v-model="form.productId"
                      :id="product.id"
                      :value="product.id"
                      role="select product"
                      v-validate="'required'"/>
                    <span class="radiobuttons__control" ></span>
                    <span :for="product" class="radiobuttons__description">{{ product.name }} - {{ product.address }}</span>
                  </label>
                </div>
              </template>
            </div>
            <tooltip v-show="errors.has('product')" class="form__validation">{{ $t('errors.product_field_required') }}</tooltip>
          </div>
        </fieldset>
        <div class="form__buttons buttonset">
          <router-link v-if="isNew" :to="{ name: 'permits' }" class="buttonset__button btn btn--secondary">
            {{ $t('common.discard') }}
          </router-link>
          <router-link v-else :to="{ name: 'permit', params: { id: $route.params.id} }" class="buttonset__button btn btn--secondary">
            {{ $t('common.discard') }}
          </router-link>
          <button type="submit" class="buttonset__button btn btn--main" @click.prevent.stop="onSubmit()">
          {{ $t('common.save') }}</button>
        </div>
      </form>

      <WarningModal v-if="tenantHasNoProducts || showWarningModal" @close="closeWarningModal">
        <h1 slot="header">{{ $t('common.warning') }}</h1>
        <p slot="body">{{ $t('permits.tenant_has_no_products') }}</p>
      </WarningModal>
    </div>
  </div>

</template>

<script>
import { mapGetters } from 'vuex';
import * as mTypes from 'store/mutation-types';
import Tooltip from 'common/Tooltip';
import ConfirmationModal from 'common/ConfirmationModal';
import WarningModal from 'common/WarningModal';

export default {
  components: {
    Tooltip,
    ConfirmationModal,
    WarningModal,
  },
  data() {
    return {
      form: {},
      selectedTenant: null,
      selectedUser: null,
      selectedVehicle: null,
      showConfirmationModal: false,
      showWarningModal: false,
      tenantHasProducts: false,
      formAlreadySubmited: false,
    };
  },
  mounted() {
    this.fetchAllData();
    if (this.$route.params.userId) {
      this.$store.dispatch('users/user/fetch', { id: this.$route.params.userId, user: this.user }).then(() => {
        this.selectedUser = this.selectedUserData.id;
      });
    }
  },
  computed: {
    isNew() {
      return !this.$route.params.id;
    },
    tenantHasNoProducts() {
      return this.isTenant() && this.products.items && this.products.items.length === 0;
    },
    ...mapGetters({
      products: 'landlord/products/products',
      permitError: 'permits/permit/error',
      permit: 'permits/permit/permit',
      tenants: 'landlord/tenants/tenants',
      user: 'user/user/user',
      users: 'landlord/users/users',
      selectedUserData: 'users/user/user',
      vehicles: 'users/vehicles/vehicles',
      permitsLimit: 'landlord/permits/limit',
      permitsKeywordFilter: 'landlord/permits/keywordFilter',
    }),
  },
  watch: {
    selectedTenant: 'fetchTenantData',
    selectedUser: 'fetchVehiclePlateNumbers',
    $route: 'fetchAllData',
    vehicles: 'preselectVehicle',
  },
  created() {
    this.$store.commit(`users/user/${mTypes.CLEAR_STATE}`);
    this.$store.commit(`permits/permit/${mTypes.CLEAR_STATE}`);
    this.$store.commit(`landlord/users/${mTypes.CLEAR_STATE}`);
    this.$store.commit(`landlord/products/${mTypes.CLEAR_STATE}`);
    this.$store.commit(`users/vehicles/${mTypes.CLEAR_STATE}`);
    this.resetValidators();
    this.createValidators();
  },
  methods: {
    hasProducts() {
      if (this.selectedTenant || this.isTenant()) {
        let productsNotFull = false;
        if (this.products.total > 0) {
          this.products.items.forEach((el) => {
            if (el.issued < el.total) {
              productsNotFull = true;
            }
          });
        }
        this.tenantHasProducts = this.products.total > 0 && productsNotFull;
      }
    },
    fetchVehiclePlateNumbers() {
      this.selectedVehicle = null;
      if (this.selectedUser) {
        this.$store.dispatch('users/vehicles/fetch', { id: this.selectedUser, user: this.user });
      }
    },
    preselectVehicle() {
      if (this.vehicles.items && this.vehicles.items.length > 0) {
        this.vehicles.items.forEach((el) => {
          if (el.default) {
            this.selectedVehicle = el.id;
          }
        });
        if (!this.selectedVehicle) {
          this.selectedVehicle = this.vehicles.items[0].id;
        }
      }
    },
    fetchTenantData() {
      this.fetchProducts();
      this.fetchUsers();
    },
    fetchUsers() {
      this.selectedUser = null;
      if (this.isTenant()) {
        this.$store.dispatch('landlord/users/fetch', { user: this.user, limit: 200 });
      } else {
        this.$store.dispatch('landlord/users/fetch', { user: this.user, partnerId: this.selectedTenant, limit: 200 });
      }
    },
    fetchAllData() {
      if (this.user) {
        if (this.$route.params.id) {
          this.fetchPermit();
        } else {
          this.$store.commit(`permits/permit/${mTypes.CLEAR_STATE}`);
          this.form = this.permit;
        }
        if (this.$route.params.userId) {
          this.selectedUser = this.$route.params.userId;
        }
        this.fetchProducts();
        if (this.isLandlord()) {
          if (this.selectedUser && !this.selectedTenant) {
            this.$store.dispatch('users/user/fetch', { id: this.selectedUser, user: this.user })
              .then(() => {
                this.selectedTenant = this.selectedUserData.partner[0].id;
              });
          }
          this.fetchTenants();
        } else {
          this.fetchUsers();
        }
      }
    },
    fetchProducts() {
      this.productId = null;
      if (this.isLandlord() && this.selectedTenant !== null && this.user.data && typeof this.selectedTenant !== 'undefined') {
        this.$store.dispatch('landlord/products/fetchTenantProducts', { user: this.user, id: this.selectedTenant, type: 'permitPack' }).then(() => {
          this.hasProducts();
        });
      } else if (this.isTenant() && this.user.data) {
        this.$store.dispatch('landlord/products/fetchTenantProducts', { user: this.user, type: 'permitPack' }).then(() => {
          this.hasProducts();
        });
      } else {
        this.products.items = {};
      }
    },
    fetchTenants() {
      if (this.user.data) {
        this.$store.dispatch('landlord/tenants/list', { user: this.user });
      }
    },
    fetchPermit() {
      if (this.user.data) {
        this.$store.dispatch('permits/permit/fetch', { id: this.$route.params.id, user: this.user }).then(() => {
          this.form = this.permit;
          if (this.permit.partner) {
            this.selectedTenant = this.permit.partner.id;
          }
          if (this.permit.product) {
            this.form.productId = this.permit.product.id;
          }
          if (this.permit.vehicle) {
            this.form.vehicleReg = this.permit.vehicle.vehicleReg;
          }
          if (this.permit.user) {
            this.selectedUser = this.permit.user.id;
          }
        });
      }
    },
    onSubmit() {
      if (this.formAlreadySubmited) {
        return;
      }
      this.$validator.validateAll(
        { selectedUser: 'userOrParkingCardNrRequired',
          parkingCardNr: this.form.parkingCardNr,
          mobile: this.form.mobilePhoneNr,
          email: this.form.email,
          product: this.form.productId,
          selectedTenant: 'selectedTenant' },
      )
        .then((success) => {
          this.$store.commit(`permits/permit/${mTypes.DATA_ERROR}`, null);
          if (!this.tenantHasProducts) {
            this.showWarningModal = true;
            return;
          }
          this.formAlreadySubmited = true;
          if (success) {
            this.form.tenantId = this.isTenant()
              ? this.user.data.selectedRole.id
              : this.selectedTenant;
            this.form.userId = this.selectedUser;
            this.form.vehicleId = this.selectedVehicle;
            this.$store.dispatch('permits/permit/savePermit', { permit: this.form, user: this.user })
              .then(() => {
                if (!this.permitError) {
                  this.form = {};
                  this.resetValidators();
                  this.$store.dispatch('landlord/permits/fetch', { user: this.user, limit: this.permitsLimit, keyword: this.permitsKeywordFilter });
                  this.$router.push({ name: 'permit', params: { id: this.permit.id } });
                }
                this.formAlreadySubmited = false;
              })
              .catch(() => {
                this.$store.commit(`permits/permit/${mTypes.DATA_ERROR}`, true);
                this.formAlreadySubmited = false;
              });
          } else {
            this.$store.commit(`permits/permit/${mTypes.DATA_ERROR}`, true);
            this.formAlreadySubmited = false;
          }
        })
        .catch(() => {
          this.$store.commit(`products/product/${mTypes.DATA_ERROR}`, true);
          this.formAlreadySubmited = false;
        });
    },
    resetValidators() {
      this.$validator.remove('validParkingCardNr');
      this.$validator.remove('userOrParkingCardNrRequired');
      this.$validator.remove('selectedTenant');
      this.$validator.clean();
    },
    createValidators() {
      this.createParkingCardValidator();
      this.createUserOrParkingCardRequiredValidator();
      this.createTenantValidator();
    },
    createParkingCardValidator() {
      this.$validator.extend('validParkingCardNr', {
        getMessage: field => `The ${field} value is not valid parking card number.`,
        validate: (value) => {
          if (value === '') {
            return true;
          }
          // check if card number is 9 characters long and contains only hex characters
          return /^[0-9A-F]{9}$/i.test(value);
        },
      });
    },
    createUserOrParkingCardRequiredValidator() {
      this.$validator.extend('userOrParkingCardNrRequired', {
        getMessage: () => 'Parking card number or user must be set',
        validate: () => {
          const parking = this.$refs.parkingCardNr.value;
          const user = this.$refs.selectedUser.value;
          return !((!parking || parking === '') && (!user || user === ''));
        },
      });
    },
    createTenantValidator() {
      this.$validator.extend('selectedTenant', {
        getMessage: () => 'Tenant must be selected',
        validate: () => this.isLandlord() && (typeof this.selectedTenant !== 'undefined' && this.selectedTenant !== null),
      });
    },
    closeWarningModal() {
      this.showWarningModal = false;
      if (this.isTenant()) {
        this.$router.push({ name: 'dashboard' });
      } else {
        this.selectedTenant = null;
      }
    },
    clearUser() {
      this.selectedUser = null;
    },
    clearVehicle() {
      this.selectedVehicle = null;
    },
    canAddPermitTo(product) {
      return product.issued < product.total ||
        (this.permit.product && product.id === this.permit.product.id);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~styles/blocks/details.scss';

.form {
  &__selectbox {
    width: 200px;
  }

  .add-vehicle {
    width: 100%;
    font-size: 12px;

    ::placeholder {
      font-size: 12px;
      padding: 15px 0 0 0;
      opacity: 0.4;
      padding: 10px 0;
      font-weight: 700;
    }

    &--btn-plus {
      float: right;
      position: relative;
      bottom: 26px;
      border: none;
      background: none;
      font-size: 17px;
      right: 4px;
      color: var(--brand-blue);
      cursor: pointer;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/permits/AddPermitView.vue